import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const GOLD = '#C9A84C';
const BG = '#050810';
const SURFACE = '#0B1120';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';

const s = {
  page: {
    background: BG,
    color: '#fff',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    minHeight: '100vh',
  },
  wrap: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '80px 24px 100px',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(201,168,76,0.12)',
    border: `1px solid rgba(201,168,76,0.3)`,
    color: GOLD,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '6px 14px',
    borderRadius: 20,
    marginBottom: 28,
  },
  h1: {
    fontSize: 'clamp(26px,5vw,38px)',
    fontWeight: 800,
    margin: '0 0 40px',
    lineHeight: 1.2,
  },
  p: {
    color: OFF_WHITE,
    fontSize: 17,
    lineHeight: 1.85,
    marginBottom: 24,
  },
  pullQuote: {
    borderLeft: `3px solid ${GOLD}`,
    paddingLeft: 24,
    margin: '40px 0',
    color: '#fff',
    fontSize: 18,
    lineHeight: 1.7,
    fontStyle: 'italic',
  },
  statBlock: {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '28px 32px',
    margin: '36px 0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 32,
  },
  stat: {
    flex: '1 1 120px',
  },
  statNum: {
    fontSize: 30,
    fontWeight: 800,
    color: GOLD,
    display: 'block',
    marginBottom: 4,
  },
  statLabel: {
    color: OFF_WHITE,
    fontSize: 13,
    lineHeight: 1.4,
  },
  divider: {
    height: 1,
    background: BORDER,
    margin: '48px 0',
  },
  signoff: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.8,
    borderTop: `1px solid ${BORDER}`,
    paddingTop: 32,
    marginTop: 48,
  },
  name: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
    marginTop: 8,
    display: 'block',
  },
  cta: {
    background: 'rgba(201,168,76,0.07)',
    border: `1px solid rgba(201,168,76,0.25)`,
    borderRadius: 16,
    padding: '40px',
    textAlign: 'center',
    marginTop: 56,
  },
  ctaTitle: {
    fontSize: 20,
    fontWeight: 800,
    marginBottom: 10,
  },
  ctaSub: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: 28,
  },
  btn: {
    display: 'inline-block',
    background: GOLD,
    color: '#000',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '16px 40px',
    borderRadius: 8,
    textDecoration: 'none',
  },
};

export default function Story() {
  return (
    <div style={s.page}>
      <Helmet>
        <title>Why I Built Mane — Christian G Flair</title>
        <meta name="description" content="I was a working barber paying $230/mo for booking software. I thought it was $30. This is why I built Mane." />
        <meta property="og:title" content="Why I Built Mane — Christian G Flair" />
        <meta property="og:description" content="I was a working barber paying $230/mo for software. I thought it was $30. So I built something better." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <div style={s.wrap}>
        <div style={s.badge}>The Founder Story</div>
        <h1 style={s.h1}>Why I Built Mane</h1>

        <p style={s.p}>
          I've been behind the chair for years. Booth renter. Built my clientele from nothing —
          word of mouth, Instagram, referrals, late nights, early mornings. The way most of us do it.
        </p>

        <p style={s.p}>
          A few years in, I moved onto booking software. Made sense. Clients could book themselves.
          No more back-and-forth texts. I thought I was leveling up.
        </p>

        <p style={s.p}>
          What I didn't do was read the fine print carefully enough.
        </p>

        <div style={s.pullQuote}>
          "I'd been telling people I paid $30 a month for booking software. I was paying $230."
        </div>

        <p style={s.p}>
          I was on Booksy. Had Boost running because that's how you get new clients through the platform.
          End of a good month — I mean a real good month — I sat down and looked at what I'd actually paid.
        </p>

        <div style={s.statBlock}>
          <div style={s.stat}>
            <span style={s.statNum}>$30</span>
            <span style={s.statLabel}>Base fee I thought I was paying</span>
          </div>
          <div style={s.stat}>
            <span style={s.statNum}>$180</span>
            <span style={s.statLabel}>Boost fees from new clients (30% each)</span>
          </div>
          <div style={s.stat}>
            <span style={s.statNum}>$230</span>
            <span style={s.statLabel}>What I was actually paying that month</span>
          </div>
        </div>

        <p style={s.p}>
          That math — played out over a year — is nearly $2,800. That's a piece of equipment.
          That's a real vacation. That's money that belonged to me because I did the work.
        </p>

        <p style={s.p}>
          I talked to barbers. Stylists. Nail techs. Estheticians. The story was the same everywhere.
          Fresha taking 20% of new clients and calling it free. Squire charging $100–$250 a month and also
          billing clients a booking fee. Vagaro starting at $23 and landing at $150 with all the pieces turned on.
        </p>

        <div style={s.pullQuote}>
          "The tools designed for independent professionals were structured to benefit from our growth.
          The better I did, the more they made. That's backwards."
        </div>

        <p style={s.p}>
          So I built Mane.
        </p>

        <p style={s.p}>
          The whole concept is simple: flat rate. No commission. No percentage of anything.
          $29 a month and you keep everything you earn. Your client books from a link — no app download,
          no account, 60 seconds — gets a text reminder, and if they go quiet, the system follows up.
          You manage your business. The tool stays out of your money.
        </p>

        <p style={s.p}>
          I'm not a software company founder who saw a market opportunity.
          I'm a barber who got tired of subsidizing a platform with my own growth.
          Mane exists because the alternative was paying someone else a percentage of my clients forever.
        </p>

        <p style={s.p}>
          I'm still behind the chair. I built this in between.
          If it helps you keep more of what you earn, that's the whole point.
        </p>

        <div style={s.signoff}>
          <span style={s.name}>— Christian G Flair</span>
          Founder, Mane · Philadelphia
          <br />
          <a href="mailto:christiangflairbiz@gmail.com" style={{ color: GOLD, fontSize: 14 }}>
            christiangflairbiz@gmail.com
          </a>
        </div>

        <div style={s.cta}>
          <div style={s.ctaTitle}>50 founding spots. Most are claimed.</div>
          <p style={s.ctaSub}>
            $199 once. $29/mo locked for life. When the spots fill,<br />
            the price goes to $59/mo and doesn't come back.
          </p>
          <Link to="/founding" style={s.btn}>Join as a Founding Member</Link>
        </div>
      </div>
    </div>
  );
}
